'use client';

import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Copy, RefreshCw, Check, Sparkles, Braces, FileText } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { Toaster } from '@/components/ui/toaster';
import { cn } from '@/lib/utils';
import { ToolPageIntro } from '../../components/seo/ToolPageIntro';
import { InformationalContent } from '../../components/seo/InformationalContent';
import { jsonEscapeContent } from '../../data/json-escape-content';
import Link from 'next/link';

const sampleJson = `{
  "name": "John Doe",
  "age": 30,
  "email": "john@example.com",
  "address": {
    "street": "123 Main St",
    "city": "New York",
    "country": "USA"
  },
  "quotes": [
    "Hello, world!",
    "This is a test",
    "Special chars: \" ' \\ \/ \b \f \n \r \t"
  ]
}`;

const sampleEscaped = '{\n  "name": \"John Doe\",\n  \"age\": 30,\n  \"email\": \"john@example.com\",\n  \"address\": {\n    \"street\": \"123 Main St\",\n    \"city\": \"New York\",\n    \"country\": \"USA\"\n  },\n  \"quotes\": [\n    \"Hello, world!\",\n    \"This is a test\",\n    \"Special chars: \\\" \' \\\\ \\\/ \\b \\f \\n \\r \\t\"\n  ]\n}';

export default function JsonEscapeTool() {
    const [input, setInput] = useState('');
    const [output, setOutput] = useState('');
    const [activeTab, setActiveTab] = useState('escape');
    const [isCopied, setIsCopied] = useState(false);
    const { toast } = useToast();
    const [error, setError] = useState('');

    useEffect(() => {
        if (input.trim() === '') {
            setOutput('');
            setError('');
            return;
        }

        try {
            if (activeTab === 'escape') {
                // Escape the input as a JSON string
                const escaped = JSON.stringify(input);
                // Remove the surrounding quotes that JSON.stringify adds
                setOutput(escaped.slice(1, -1));
                setError('');
            } else {
                // For unescaping, we need to create a valid JSON string first
                const jsonString = `"${input}"`;
                const unescaped = JSON.parse(jsonString);
                setOutput(unescaped);
                setError('');
            }
        } catch (error) {
            setError('Error: Invalid JSON input for unescaping');
            setOutput('');
        }
    }, [input, activeTab]);

    const handleCopy = () => {
        if (!output) return;
        navigator.clipboard.writeText(output);
        setIsCopied(true);
        toast({
            title: 'Copied to clipboard!',
            description: 'The text has been copied to your clipboard.',
            variant: 'default',
        });
        setTimeout(() => setIsCopied(false), 2000);
    };

    const handleClear = () => {
        setInput('');
        setOutput('');
        setError('');
    };

    const handleSwitch = () => {
        setInput(output);
        setActiveTab(prev => (prev === 'escape' ? 'unescape' : 'escape'));
    };

    const loadSample = () => {
        setInput(activeTab === 'escape' ? sampleJson : sampleEscaped);
    };

    const formatJson = (jsonString: string) => {
        try {
            const json = JSON.parse(jsonString);
            return JSON.stringify(json, null, 2);
        } catch {
            return jsonString;
        }
    };

    return (
        <div className="container mx-auto px-4 py-8 max-w-7xl">
            <div className="text-center mb-10 space-y-2">
                <div className="inline-flex items-center justify-center px-4 py-1.5 rounded-full bg-primary/10 text-primary text-sm font-medium mb-3">
                    <Sparkles className="h-4 w-4 mr-2" />
                    Developer Tool
                </div>
                <ToolPageIntro title="JSON Escape/Unescape">
                Escape or unescape JSON strings and special characters with our free online tool. Perfect for safely including JSON in URLs, HTML, and JavaScript.
            </ToolPageIntro>
            </div>

            <div className="w-full max-w-6xl mx-auto mb-6">
                <Tabs
                    value={activeTab}
                    onValueChange={setActiveTab}
                    className="w-full"
                >
                    <TabsList className="w-full max-w-md mx-auto grid grid-cols-2 h-12 bg-muted/50 backdrop-blur-sm">
                        <TabsTrigger
                            value="escape"
                            className="data-[state=active]:bg-background data-[state=active]:shadow-sm data-[state=active]:text-primary relative group"
                        >
                            <Braces className="h-4 w-4 mr-2 text-emerald-500 group-data-[state=active]:animate-pulse" />
                            Escape
                        </TabsTrigger>
                        <TabsTrigger
                            value="unescape"
                            className="data-[state=active]:bg-background data-[state=active]:shadow-sm data-[state=active]:text-primary relative group"
                        >
                            <FileText className="h-4 w-4 mr-2 text-blue-500 group-data-[state=active]:animate-pulse" />
                            Unescape
                        </TabsTrigger>
                    </TabsList>
                </Tabs>
            </div>

            <div className="w-full max-w-7xl mx-auto grid grid-cols-12 gap-2 lg:gap-4 items-stretch">
                <div className="col-span-12 lg:col-span-5 h-full flex flex-col">
                    <Card className="border-0 shadow-lg overflow-hidden flex-1 flex flex-col h-full">
                        <CardHeader className="bg-gradient-to-r from-primary/5 to-primary/10 border-b">
                            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                                <div>
                                    <CardTitle className="text-xl font-semibold">
                                        {activeTab === 'escape' ? 'Original JSON' : 'Escaped JSON'}
                                    </CardTitle>
                                    <CardDescription className="mt-1">
                                        {activeTab === 'escape'
                                            ? 'Enter your JSON to escape special characters'
                                            : 'Paste escaped JSON to convert back to normal'}
                                    </CardDescription>
                                </div>
                                <div className="flex items-center gap-2">
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={loadSample}
                                        className="gap-1.5"
                                    >
                                        <Sparkles className="h-3.5 w-3.5" />
                                        Sample
                                    </Button>
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={handleClear}
                                        disabled={!input}
                                        className="text-muted-foreground hover:text-foreground"
                                    >
                                        Clear
                                    </Button>
                                </div>
                            </div>
                        </CardHeader>
                        <CardContent className="p-0 flex-1 flex flex-col">
                            <div className="flex-1 flex flex-col">
                                <Textarea
                                    value={input}
                                    onChange={(e) => setInput(e.target.value)}
                                    placeholder={
                                        activeTab === 'escape'
                                            ? 'e.g., {"name":"John", "age":30, "city":"New York"}'
                                            : 'e.g., {\"name\":\"John\", \"age\":30, \"city\":\"New York\"}'
                                    }
                                    className="min-h-[300px] font-mono text-sm border-0 focus-visible:ring-0 focus-visible:ring-offset-0 rounded-none resize-none"
                                    spellCheck={false}
                                />
                            </div>
                        </CardContent>
                    </Card>
                </div>

                <div className="col-span-12 lg:col-span-2 flex items-center justify-center my-2 lg:my-0">
                    <div className="relative w-full h-full flex items-center justify-center">
                        <div className="hidden lg:block absolute top-1/2 left-0 right-0 h-px bg-gradient-to-r from-transparent via-border to-transparent -z-10" />
                        <Button
                            variant="outline"
                            size="icon"
                            onClick={handleSwitch}
                            className="rounded-full w-14 h-14 shadow-lg hover:shadow-primary/20 hover:bg-primary/5 transition-all duration-300 bg-background border-2 border-primary/20 hover:border-primary/50 flex-shrink-0"
                            title="Switch between escape/unescape"
                        >
                            <RefreshCw className="h-6 w-6 text-primary" />
                        </Button>
                    </div>
                </div>

                <div className="col-span-12 lg:col-span-5 h-full flex flex-col">
                    <Card className="border-0 shadow-lg overflow-hidden flex-1 flex flex-col h-full">
                        <CardHeader className="bg-gradient-to-r from-primary/5 to-primary/10 border-b">
                            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                                <div>
                                    <CardTitle className="text-xl font-semibold">
                                        {activeTab === 'escape' ? 'Escaped JSON' : 'Decoded JSON'}
                                    </CardTitle>
                                    <CardDescription className="mt-1">
                                        {activeTab === 'escape'
                                            ? 'Your JSON with escaped characters'
                                            : 'Your unescaped JSON'}
                                    </CardDescription>
                                </div>
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={handleCopy}
                                    disabled={!output}
                                    className={cn(
                                        'gap-1.5 transition-all',
                                        isCopied
                                            ? 'bg-green-500/10 border-green-500/20 text-green-600 hover:bg-green-500/20'
                                            : 'hover:bg-primary/5'
                                    )}
                                >
                                    {isCopied ? (
                                        <Check className="h-4 w-4" />
                                    ) : (
                                        <Copy className="h-4 w-4" />
                                    )}
                                    {isCopied ? 'Copied!' : 'Copy'}
                                </Button>
                            </div>
                        </CardHeader>
                        <CardContent className="p-0 flex-1 flex flex-col">
                            <div className="relative flex-1 flex flex-col min-h-[300px]">
                                <Textarea
                                    value={output}
                                    readOnly
                                    className={cn(
                                        'flex-1 font-mono text-sm border-0 bg-muted/20',
                                        'focus-visible:ring-0 focus-visible:ring-offset-0 rounded-none resize-none',
                                        error && 'text-destructive/70'
                                    )}
                                    placeholder={
                                        activeTab === 'escape'
                                            ? 'Escaped JSON will appear here...'
                                            : 'Decoded JSON will appear here...'
                                    }
                                />
                                {!output && !error && (
                                    <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                                        <p className="text-muted-foreground/50 text-sm text-center px-4">
                                            {activeTab === 'escape'
                                                ? 'Your escaped JSON will appear here'
                                                : 'Your decoded JSON will appear here'}
                                        </p>
                                    </div>
                                )}
                                {error && (
                                    <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                                        <p className="text-destructive/70 text-sm text-center px-4">
                                            {error}
                                        </p>
                                    </div>
                                )}
                            </div>
                        </CardContent>
                    </Card>
                </div>
            </div>

            {/* Informational Content */}
            <div className="mt-12">
                <InformationalContent
                    title={jsonEscapeContent.title}
                    description={jsonEscapeContent.description}
                    content={jsonEscapeContent.content}
                    faqs={jsonEscapeContent.faqs}
                    relatedTools={jsonEscapeContent.relatedTools}
                />
            </div>

            <Toaster />
        </div>
    );
}
